import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Mail, X } from 'lucide-react';
import Sidebar from '../pages/dashboard/components/Sidebar.tsx';
import MobileMenu from '../pages/dashboard/components/MobileMenu.tsx';
import { authService, AUTH_CHANGED_EVENT } from '../services/auth.service.ts';
import { getEmailVerificationStatus } from '../utils/emailVerification.ts';

function EmailVerifyBanner() {
  const [user, setUser] = useState(() => authService.getUser());
  const [dismissed, setDismissed] = useState(false);
  const [resending, setResending] = useState(false);
  const [note, setNote] = useState('');

  useEffect(() => {
    const sync = () => setUser(authService.getUser());
    window.addEventListener(AUTH_CHANGED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(AUTH_CHANGED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const verification = getEmailVerificationStatus(user);
  if (!verification.needsVerification || verification.isLocked || dismissed) {
    return null;
  }

  const handleResend = async () => {
    setResending(true);
    setNote('');
    try {
      await authService.resendVerification();
      setNote('Doğrulama maili gönderildi.');
    } catch (err: any) {
      setNote(err?.message || 'Gönderilemedi.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <Mail className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm text-amber-100 font-medium">
            E-postanı doğrula — {verification.daysLeft} günün kaldı
          </p>
          <p className="text-xs text-amber-200/80 mt-0.5">
            {user?.email ? `${user.email} adresine` : 'Adresine'} gönderilen bağlantıya tıkla.
            Süre dolunca panele erişim kısıtlanır.
          </p>
          {note ? <p className="text-xs text-amber-100 mt-1">{note}</p> : null}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={handleResend}
          disabled={resending}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 text-xs font-semibold transition disabled:opacity-60"
        >
          {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          Tekrar gönder
        </button>
        <Link
          to="/verify-email"
          className="px-3 py-1.5 rounded-lg text-xs font-semibold text-amber-100 hover:bg-white/5 transition"
        >
          Detay
        </Link>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="p-1.5 rounded-lg text-amber-200/70 hover:text-amber-100 hover:bg-white/5"
          aria-label="Kapat"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function DashboardLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-16">
      <div className="lg:hidden">
        <MobileMenu />
      </div>

      <div className="flex">
        <aside className="hidden lg:block w-64 xl:w-72 shrink-0 p-4 pt-6">
          <Sidebar />
        </aside>

        <main className="flex-1 p-4 md:p-6 lg:p-8 min-w-0">
          <div className="max-w-7xl mx-auto">
            <EmailVerifyBanner />
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export default DashboardLayout;
